/**
 * Glowup API contract — frontend.
 *
 * MIRROR: keep in sync with functions/contracts/glowupApi.js.
 * Any change to a shape, code, or vector list MUST update both copies in the same commit.
 *
 * Imported by Lane C frontend (src/pages/CreateGlowup, /Glowups, /GlowupDetail,
 * /GlowupJudge and the src/components/glowup/* tree).
 *
 * @typedef {("skin_quality"|"hair_scalp"|"facial_hair"|"brows_lashes"|"mouth_smile"|"definition")} VectorName
 * @typedef {VectorName | "biggest_overall"} JudgmentTarget
 *
 * @typedef {Object} GlowupSideScanInput
 * @property {"scan"} kind
 * @property {string} scan_id
 *
 * @typedef {Object} GlowupSideFreshInput
 * @property {"fresh"} kind
 * @property {string} imageBase64
 * @property {string} mimeType
 * @property {string} taken_at        // ISO 8601
 *
 * @typedef {GlowupSideScanInput | GlowupSideFreshInput} GlowupSideInput
 *
 * @typedef {Object} GlowupCreateResponse
 * @property {"success"} status
 * @property {string} glowup_id
 * @property {string} before_scan_id
 * @property {string} after_scan_id
 *
 * @typedef {Object} GlowupDeckCard
 * @property {string} glowup_id
 * @property {{ anonymized_image_url: string, taken_at: string }} before
 * @property {{ anonymized_image_url: string, taken_at: string }} after
 * @property {string} time_gap_label
 *
 * @typedef {Object} GlowupJudgmentInput
 * @property {string} glowup_id
 * @property {JudgmentTarget[]} selected_vectors
 * @property {Object<string, number>} [vector_scores]
 * @property {"right" | "left"} direction
 *
 * @typedef {Object} GlowupJudgmentResponse
 * @property {"success"} status
 * @property {number} responses_total_for_user
 * @property {boolean} reached_quota
 *
 * @typedef {Object} PeerScoreVectorRow
 * @property {JudgmentTarget} name
 * @property {number} mentions
 * @property {number|null} mean_score
 * @property {number} score_count
 *
 * @typedef {Object} PeerScoreReadoutShape
 * @property {PeerScoreVectorRow[]} vectors
 * @property {number} responses_seen
 * @property {number} responses_positive
 * @property {VectorName|null} biggest_vector
 *
 * @typedef {Object} OwnGlowupRow
 * @property {string} glowup_id
 * @property {string} created_at
 * @property {string} before_thumbnail_url
 * @property {string} after_thumbnail_url
 * @property {string} before_taken_at
 * @property {string} after_taken_at
 * @property {number} responses_seen
 * @property {VectorName|null} biggest_vector
 *
 * @typedef {Object} GlowupErrorResponse
 * @property {"error"} status
 * @property {GlowupErrorCode} error_code
 * @property {string} error_message
 * @property {string} [scan_id]
 *
 * @typedef {("UNAUTHENTICATED"|"ENTITLEMENT_REQUIRED"|"FORBIDDEN"|"NOT_FOUND"
 *   |"MISSING_TAKEN_AT"|"INVALID_INPUT"|"QUOTA_REQUIRED"|"DUPLICATE_FEEDBACK"
 *   |"DECK_EMPTY"|"MODEL_REFUSED"|"MODEL_MALFORMED"|"NSFW"|"NO_FACE"
 *   |"MULTIPLE_FACES"|"UNDER_16"|"INTERNAL")} GlowupErrorCode
 */

// Kept in sync with the Oracle feedback taxonomy (see contracts/taxonomy.js
// CATEGORIES). Glowup judges the 6 MODIFIABLE feedback categories — the FIXED
// 7th category (bone_structure) is excluded because bone cannot improve in a
// before/after, so it is not a meaningful peer-judged improvement vector.
export const GLOWUP_VECTORS = Object.freeze([
  "skin_quality", "hair_scalp", "facial_hair", "brows_lashes", "mouth_smile", "definition",
]);

// "biggest_overall" is a separate optional holistic judgment target — judges
// can additionally mark "this is a big glowup overall" without selecting any
// per-vector improvements. Tracked in peer_score under its own key.
export const HOLISTIC_TARGET = "biggest_overall";

export const JUDGMENT_TARGETS = Object.freeze([
  ...GLOWUP_VECTORS,
  HOLISTIC_TARGET,
]);

// Short display labels for the glowup judge/encourage chips ONLY. The Oracle
// feedback taxonomy labels ("Facial Hair", "Facial Definition", "Skin Quality")
// are too long for the compact Tinder-pace chips — they truncate, and "Facial
// Hair"/"Facial Definition" both collapse to "FACIA…". These short labels keep
// the chips legible while the underlying keys stay synced to the taxonomy. The
// verdict/feedback page still uses the full labels via VECTOR_DISPLAY_LABELS.
export const GLOWUP_VECTOR_LABELS = Object.freeze({
  skin_quality: "SKIN",
  hair_scalp: "HAIR",
  facial_hair: "BEARD",
  brows_lashes: "BROWS",
  mouth_smile: "MOUTH",
  definition: "DEFINITION",
  [HOLISTIC_TARGET]: "OVERALL",
});

/** Short uppercase chip label for a glowup judgment target. */
export function glowupVectorLabel(target) {
  return GLOWUP_VECTOR_LABELS[target] || String(target).toUpperCase();
}

export const GLOWUP_ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: "UNAUTHENTICATED",
  ENTITLEMENT_REQUIRED: "ENTITLEMENT_REQUIRED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  MISSING_TAKEN_AT: "MISSING_TAKEN_AT",
  INVALID_INPUT: "INVALID_INPUT",
  QUOTA_REQUIRED: "QUOTA_REQUIRED",
  DUPLICATE_FEEDBACK: "DUPLICATE_FEEDBACK",
  DECK_EMPTY: "DECK_EMPTY",
  MODEL_REFUSED: "MODEL_REFUSED",
  MODEL_MALFORMED: "MODEL_MALFORMED",
  NSFW: "NSFW",
  NO_FACE: "NO_FACE",
  MULTIPLE_FACES: "MULTIPLE_FACES",
  UNDER_16: "UNDER_16",
  INTERNAL: "INTERNAL",
});

// Vote-to-see threshold — judge N glowups before reading own peer-score.
export const PEER_SCORE_VOTE_QUOTA = 5;

// Suppress noisy means until at least this many judges have scored a vector.
export const MEAN_SCORE_MIN_COUNT = 3;

// time_gap_label boundary set. Computed from (after.taken_at - before.taken_at).
// Lane D may refine copy values; keys are stable.
//
//   ┌────────────────────────────────────────────────────────────┐
//   │ < 14 days   -> WEEK                                         │
//   │ 14d - 89d   -> WEEKS                                        │
//   │ 90d - 364d  -> MONTHS                                       │
//   │ >= 365d     -> YEARS                                        │
//   └────────────────────────────────────────────────────────────┘
export const TIME_GAP_BOUNDARIES = Object.freeze({
  WEEK_MAX_DAYS: 14,
  WEEKS_MAX_DAYS: 90,
  MONTHS_MAX_DAYS: 365,
});

export function computeTimeGapLabel(beforeIso, afterIso) {
  const beforeMs = Date.parse(beforeIso);
  const afterMs = Date.parse(afterIso);
  if (Number.isNaN(beforeMs) || Number.isNaN(afterMs) || afterMs <= beforeMs) {
    return "PERIOD";
  }
  const days = (afterMs - beforeMs) / 86400000;
  if (days < TIME_GAP_BOUNDARIES.WEEK_MAX_DAYS) return "WEEK";
  if (days < TIME_GAP_BOUNDARIES.WEEKS_MAX_DAYS) return "WEEKS";
  if (days < TIME_GAP_BOUNDARIES.MONTHS_MAX_DAYS) return "MONTHS";
  return "YEARS";
}

/** @param {unknown} obj @returns {obj is GlowupCreateResponse} */
export function isGlowupCreateResponse(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && /** @type {any} */ (obj).status === "success"
      && /** @type {any} */ (obj).glowup_id
      && /** @type {any} */ (obj).before_scan_id
      && /** @type {any} */ (obj).after_scan_id,
  );
}

/** @param {unknown} obj @returns {obj is GlowupErrorResponse} */
export function isGlowupErrorResponse(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && /** @type {any} */ (obj).status === "error"
      && GLOWUP_ERROR_CODES[/** @type {any} */ (obj).error_code],
  );
}

/**
 * Sample fixture used by tests and storybook for the deck card render.
 * @type {GlowupDeckCard}
 */
export const SAMPLE_GLOWUP_DECK_CARD = Object.freeze({
  glowup_id: "00000000-0000-0000-0000-000000000000",
  before: {
    anonymized_image_url: "/sample/before.jpg",
    taken_at: "2025-12-01T00:00:00.000Z",
  },
  after: {
    anonymized_image_url: "/sample/after.jpg",
    taken_at: "2026-06-01T00:00:00.000Z",
  },
  time_gap_label: "MONTHS",
});
